import { useState, useCallback, useEffect, useRef } from 'react';
import { useComponentVisibility } from '@/hooks/useComponentVisibility';
import type { RelatedEventsData, PromotedEvent } from '../types';
import type { BasicInfo } from '@/types';
import { sdkRequest } from '@eventbrite/legacy-infra.http';
import { getLastRecentSearchTerms, getLastRecentViewsList } from '@/utils/utils';
import { ORGANIZERIDS_EXCLUDE } from '../../../../common/constants';
import { Stage } from '@eventbrite/webapp-constants';

const DEFAULT_RELATED_PROMOTED_SLOTS = 2;
const DENSE_RELATED_PROMOTED_SLOTS = 3;

const isElementVisible = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;

  return rect.top < windowHeight && rect.bottom > 0 && rect.left < windowWidth && rect.right > 0;
};

export const useRelatedAndPromotedEventsLazy = (
  eventBasicInfo: BasicInfo,
  stage: Stage,
  eventId?: string,
  isBodyRendered?: boolean,
  isDenseAdsEnabled: boolean = false,
) => {
  const [relatedEvents, setRelatedEvents] = useState<RelatedEventsData | null>(null);
  const [promotedEvents, setPromotedEvents] = useState<PromotedEvent[] | null>(null);
  const [isLoadingRelatedEvents, setIsLoadingRelatedEvents] = useState(false);
  const [isLoadingPromotedEvents, setIsLoadingPromotedEvents] = useState(false);

  // Keep the latest flag in a ref so fetchPromotedEvents can stay
  // referentially stable AND so the initial fetch always reads the most
  // recent value at fetch time. The ref is the entire late-Orion-settle
  // story: there is intentionally no follow-up refetch effect — see below.
  const isDenseAdsEnabledRef = useRef(isDenseAdsEnabled);
  isDenseAdsEnabledRef.current = isDenseAdsEnabled;

  // Tracks whether the initial fetch has run. Guards the legacy "already
  // visible on mount" useEffect against re-running when fetchAllEvents'
  // identity changes.
  const hasFetchedRef = useRef(false);

  const fetchRelatedEvents = useCallback(async () => {
    setIsLoadingRelatedEvents(true);
    try {
      if (eventBasicInfo.organizer?.id && ORGANIZERIDS_EXCLUDE[stage].includes(eventBasicInfo.organizer?.id)) {
        setRelatedEvents(null);
        setIsLoadingRelatedEvents(false);
        return;
      }
      // Always use relative URL - the local endpoint will proxy to QA server when needed
      const fetchUrl = `/e/api/${eventId}/related-events?eventOrganizerId=${eventBasicInfo.organizer?.id}`;

      const relatedEventsResponse = await fetch(fetchUrl);

      if (relatedEventsResponse.ok) {
        const data = await relatedEventsResponse.json();
        setRelatedEvents(data);
      } else {
        console.error('Failed to fetch related events: Response not ok');
      }
    } catch (err) {
      console.error('Failed to fetch related events:', err);
    } finally {
      setIsLoadingRelatedEvents(false);
    }
  }, [eventId, eventBasicInfo.organizer?.id, stage]);

  const fetchPromotedEvents = useCallback(async () => {
    setIsLoadingPromotedEvents(true);
    try {
      if (eventBasicInfo.organizer?.id && ORGANIZERIDS_EXCLUDE[stage].includes(eventBasicInfo.organizer?.id)) {
        setPromotedEvents(null);
        setIsLoadingPromotedEvents(false);
        return;
      }
      const options = getPromotedEventsOptions(eventBasicInfo, isDenseAdsEnabledRef.current);
      const promotedEventsResponse = await sdkRequest(
        `/promoted/events`,
        options,
        process.env.NEXT_PUBLIC_PROXY_API_V3 ? '/e' : '',
      );

      if (promotedEventsResponse) {
        setPromotedEvents(promotedEventsResponse);
      } else {
        setPromotedEvents(null);
        console.error('Failed to fetch promoted events');
      }
    } catch (err) {
      setPromotedEvents(null);
      console.error('Failed to fetch promoted events:', err);
    } finally {
      setIsLoadingPromotedEvents(false);
    }
  }, [eventBasicInfo, stage]);

  const fetchAllEvents = useCallback(async () => {
    if (!eventId || !isBodyRendered) {
      return;
    }

    hasFetchedRef.current = true;
    await Promise.all([fetchRelatedEvents(), fetchPromotedEvents()]);
  }, [eventId, isBodyRendered, fetchRelatedEvents, fetchPromotedEvents]);

  const elementRef = useComponentVisibility(fetchAllEvents, {
    rootMargin: '50px', // Pre-load when 50px away from viewport
  });

  useEffect(() => {
    if (!isBodyRendered || hasFetchedRef.current) {
      return;
    }

    const element = elementRef.current;
    if (!element) {
      return;
    }

    if (isElementVisible(element)) {
      fetchAllEvents();
    }
  }, [isBodyRendered, fetchAllEvents, elementRef]);

  // There is intentionally NO follow-up refetch when isDenseAdsEnabled flips
  // after the initial fetch. A refetch would either (a) cycle
  // isLoadingPromotedEvents and re-fire the PicoX slate impression in
  // useRelatedEventsPicoXTracking — the exact pollution its JSDoc warns
  // against — or (b) silently mutate `eventsToShow` and re-flash the
  // LoadingSkeleton once the consumer reads `isLoading`. Treatment fidelity
  // for users whose Orion settles after visibility is a tolerable loss; an
  // inflated `related_eb_events_recommended` impression count is not.
  // The initial fetch already reads `isDenseAdsEnabledRef.current` at request
  // time, so the common path (Orion settles before visibility) lands clean
  // treatment without any refetch.

  return {
    relatedEvents,
    promotedEvents,
    isLoading: isLoadingRelatedEvents || isLoadingPromotedEvents,
    elementRef,
  };
};

/**
 * Creates the payload for promoted events API call
 *
 * @param eventBasicInfo - Basic info about the event
 * @param isDenseAdsEnabled - Whether the dense related-events ads Orion gate
 *   (`enable_increase_impressions_on_related_events`) is on. Bumps `slots` from 2 → 3
 *   and enables the online fallback strategy.
 * @returns Request options for the API call
 */
const getPromotedEventsOptions = (eventBasicInfo: BasicInfo, isDenseAdsEnabled: boolean): RequestInit => {
  const geoPoint =
    eventBasicInfo?.isOnline || !eventBasicInfo?.venue?.address?.latitude || !eventBasicInfo?.venue?.address?.longitude
      ? undefined
      : {
          lat: eventBasicInfo?.venue?.address?.latitude,
          lon: eventBasicInfo?.venue?.address?.longitude,
          radius: 50,
        };

  const slots = isDenseAdsEnabled ? DENSE_RELATED_PROMOTED_SLOTS : DEFAULT_RELATED_PROMOTED_SLOTS;

  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      slots,
      page: 1,
      interface: 'related_events',
      app: 'web',
      ...(isDenseAdsEnabled ? { fallback_strategy: 'online_no_local_fill' } : {}),
      personalized_options: {
        recent_searches: getLastRecentSearchTerms(10),
        recent_views: getLastRecentViewsList(10),
      },
      search: {
        dates: ['current_future'],
        related_event: {
          id: parseInt(eventBasicInfo?.id),
          name: eventBasicInfo?.name,
          summary: eventBasicInfo?.summary,
        },
        place: {
          is_online: eventBasicInfo?.isOnline,
          geo_point: geoPoint,
          place_id: eventBasicInfo?.venue?.id,
        },
        client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    }),
  };
};
